import React, { useState, useEffect, useCallback } from 'react';
import { useEditor, EditorContent, BubbleMenu, FloatingMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Typography from '@tiptap/extension-typography';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { FaBold, FaItalic, FaUnderline, FaStrikethrough, FaListUl, FaListOl, FaQuoteLeft, FaLink, FaUnlink, FaHighlighter, FaHeading, FaCode, FaMinus, FaCheckSquare } from 'react-icons/fa';

// Slash command menu for Notion-like experience
const SlashCommandMenu = ({ editor, isOpen, setIsOpen }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  const commands = [
    { title: 'Text', description: 'Just start writing', icon: '📝', command: () => editor.chain().focus().setParagraph().run() },
    { title: 'Heading 1', description: 'Big section heading', icon: 'H1', command: () => editor.chain().focus().toggleHeading({ level: 1 }).run() },
    { title: 'Heading 2', description: 'Medium section heading', icon: 'H2', command: () => editor.chain().focus().toggleHeading({ level: 2 }).run() },
    { title: 'Heading 3', description: 'Small section heading', icon: 'H3', command: () => editor.chain().focus().toggleHeading({ level: 3 }).run() },
    { title: 'To-do List', description: 'Track tasks with a to-do list', icon: '✓', command: () => editor.chain().focus().toggleTaskList().run() },
    { title: 'Bullet List', description: 'Create a simple bullet list', icon: '•', command: () => editor.chain().focus().toggleBulletList().run() },
    { title: 'Numbered List', description: 'Create a numbered list', icon: '1.', command: () => editor.chain().focus().toggleOrderedList().run() },
    { title: 'Quote', description: 'Capture a quote', icon: '"', command: () => editor.chain().focus().toggleBlockquote().run() },
    { title: 'Divider', description: 'Visual divider between sections', icon: '—', command: () => editor.chain().focus().setHorizontalRule().run() },
    { title: 'Code Block', description: 'Add technical content', icon: '</>', command: () => editor.chain().focus().toggleCodeBlock().run() },
  ];
  
  const filteredCommands = query
    ? commands.filter(command => 
        command.title.toLowerCase().includes(query.toLowerCase()) || 
        command.description.toLowerCase().includes(query.toLowerCase())
      )
    : commands;

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((selectedIndex + 1) % filteredCommands.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((selectedIndex - 1 + filteredCommands.length) % filteredCommands.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredCommands[selectedIndex]) {
        filteredCommands[selectedIndex].command();
        setIsOpen(false);
        setQuery('');
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      setQuery('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="absolute z-50 bg-white rounded-lg shadow-xl border border-gray-200 w-64 max-h-80 overflow-y-auto" 
         style={{ top: '100%', left: '0' }}>
      <div className="p-2 sticky top-0 bg-white border-b border-gray-100">
        <input
          type="text"
          className="w-full p-2 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
          placeholder="Search commands..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
      </div>
      <div className="py-2">
        {filteredCommands.map((command, index) => (
          <button
            key={command.title}
            className={`w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center ${
              index === selectedIndex ? 'bg-purple-50' : ''
            }`}
            onClick={() => {
              command.command();
              setIsOpen(false);
              setQuery('');
            }}
          >
            <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded mr-3 text-gray-700">
              {command.icon}
            </div>
            <div>
              <div className="font-medium">{command.title}</div>
              <div className="text-xs text-gray-500">{command.description}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// Bubble menu that appears when text is selected
const TextBubbleMenu = ({ editor }) => {
  if (!editor) return null;

  return (
    <BubbleMenu editor={editor} tippyOptions={{ duration: 100 }}>
      <div className="flex bg-white rounded-lg shadow-lg border border-gray-200 p-1">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-100 ${editor.isActive('bold') ? 'bg-purple-100 text-purple-800' : 'text-gray-700'}`}
        >
          <FaBold size={14} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-100 ${editor.isActive('italic') ? 'bg-purple-100 text-purple-800' : 'text-gray-700'}`}
        >
          <FaItalic size={14} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-2 rounded hover:bg-gray-100 ${editor.isActive('underline') ? 'bg-purple-100 text-purple-800' : 'text-gray-700'}`}
        >
          <FaUnderline size={14} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={`p-2 rounded hover:bg-gray-100 ${editor.isActive('strike') ? 'bg-purple-100 text-purple-800' : 'text-gray-700'}`}
        >
          <FaStrikethrough size={14} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          className={`p-2 rounded hover:bg-gray-100 ${editor.isActive('highlight') ? 'bg-purple-100 text-purple-800' : 'text-gray-700'}`}
        >
          <FaHighlighter size={14} />
        </button>
        <div className="border-r border-gray-200 mx-1"></div>
        <button
          onClick={() => {
            const url = window.prompt('URL');
            if (url) {
              editor.chain().focus().setLink({ href: url }).run();
            }
          }}
          className={`p-2 rounded hover:bg-gray-100 ${editor.isActive('link') ? 'bg-purple-100 text-purple-800' : 'text-gray-700'}`}
        >
          <FaLink size={14} />
        </button>
        {editor.isActive('link') && (
          <button
            onClick={() => editor.chain().focus().unsetLink().run()}
            className="p-2 rounded hover:bg-gray-100 text-gray-700"
          >
            <FaUnlink size={14} />
          </button>
        )}
      </div>
    </BubbleMenu>
  );
};

// Floating menu that appears when a new line is created
const NewLineMenu = ({ editor }) => {
  if (!editor) return null;

  return (
    <FloatingMenu editor={editor} tippyOptions={{ duration: 100 }}>
      <div className="flex bg-white rounded-lg shadow-lg border border-gray-200 p-1">
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`p-2 rounded hover:bg-gray-100 ${editor.isActive('heading', { level: 1 }) ? 'bg-purple-100 text-purple-800' : 'text-gray-700'}`}
        >
          <FaHeading size={14} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`p-2 rounded hover:bg-gray-100 ${editor.isActive('heading', { level: 2 }) ? 'bg-purple-100 text-purple-800' : 'text-gray-700'}`}
        >
          <FaHeading size={12} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-gray-100 ${editor.isActive('bulletList') ? 'bg-purple-100 text-purple-800' : 'text-gray-700'}`}
        >
          <FaListUl size={14} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-gray-100 ${editor.isActive('orderedList') ? 'bg-purple-100 text-purple-800' : 'text-gray-700'}`}
        >
          <FaListOl size={14} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleTaskList().run()}
          className={`p-2 rounded hover:bg-gray-100 ${editor.isActive('taskList') ? 'bg-purple-100 text-purple-800' : 'text-gray-700'}`}
        >
          <FaCheckSquare size={14} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={`p-2 rounded hover:bg-gray-100 ${editor.isActive('blockquote') ? 'bg-purple-100 text-purple-800' : 'text-gray-700'}`}
        >
          <FaQuoteLeft size={14} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          className={`p-2 rounded hover:bg-gray-100 ${editor.isActive('codeBlock') ? 'bg-purple-100 text-purple-800' : 'text-gray-700'}`}
        >
          <FaCode size={14} />
        </button>
        <button
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          className="p-2 rounded hover:bg-gray-100 text-gray-700"
        >
          <FaMinus size={14} />
        </button>
      </div>
    </FloatingMenu>
  );
};

const TipTapEditor = ({ content, onChange, placeholder, readOnly = false }) => {
  const [slashMenuOpen, setSlashMenuOpen] = useState(false);
  
  // Handle slash command
  const onKeyDown = useCallback((props) => {
    const { event } = props;
    if (event.key === '/' && !slashMenuOpen) {
      setSlashMenuOpen(true);
    }
  }, [slashMenuOpen]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: placeholder || 'Type "/" for commands or just start writing...',
      }),
      Highlight,
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      Typography,
      Underline,
      Link.configure({
        openOnClick: false,
      }),
    ],
    content: content || '',
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML());
      }
    },
    onKeyDown,
  });

  useEffect(() => {
    if (editor && content !== undefined && editor.getHTML() !== content) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  // Close slash menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (slashMenuOpen) {
        setSlashMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [slashMenuOpen]);

  return (
    <div className="relative border border-gray-300 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
      {!readOnly && editor && (
        <>
          {/* Show a subtle hint for slash commands when editor is empty */}
          {editor.isEmpty && (
            <div className="absolute top-16 left-1/2 transform -translate-x-1/2 text-gray-400 text-sm bg-gray-50 px-3 py-1 rounded-full shadow-sm pointer-events-none">
              Type "/" for commands
            </div>
          )}
          <div className="flex items-center p-2 border-b border-gray-200 bg-gray-50">
            <button
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={`p-2 rounded hover:bg-gray-100 ${editor.isActive('bold') ? 'bg-gray-200' : ''}`}
              title="Bold"
            >
              <FaBold size={14} />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={`p-2 rounded hover:bg-gray-100 ${editor.isActive('italic') ? 'bg-gray-200' : ''}`}
              title="Italic"
            >
              <FaItalic size={14} />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={`p-2 rounded hover:bg-gray-100 ${editor.isActive('underline') ? 'bg-gray-200' : ''}`}
              title="Underline"
            >
              <FaUnderline size={14} />
            </button>
            <div className="border-r border-gray-300 mx-2 h-6"></div>
            <button
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              className={`p-2 rounded hover:bg-gray-100 ${editor.isActive('heading', { level: 1 }) ? 'bg-gray-200' : ''}`}
              title="Heading 1"
            >
              <FaHeading size={14} />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={`p-2 rounded hover:bg-gray-100 ${editor.isActive('bulletList') ? 'bg-gray-200' : ''}`}
              title="Bullet List"
            >
              <FaListUl size={14} />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleTaskList().run()}
              className={`p-2 rounded hover:bg-gray-100 ${editor.isActive('taskList') ? 'bg-gray-200' : ''}`}
              title="Task List"
            >
              <FaCheckSquare size={14} />
            </button>
          </div>
          <TextBubbleMenu editor={editor} />
          <NewLineMenu editor={editor} />
        </>
      )}
      
      <div className="relative">
        <EditorContent 
          editor={editor} 
          className={`prose prose-black max-w-none p-6 min-h-[300px] focus:outline-none ${readOnly ? 'prose-sm' : ''} transition-all duration-200`} 
          style={{ 
            color: 'black',
            width: '100%',
            maxWidth: '800px',
            margin: '0 auto',
            backgroundColor: editor && editor.isFocused ? '#fafafa' : 'white'
          }}
        />
        {slashMenuOpen && editor && (
          <SlashCommandMenu 
            editor={editor} 
            isOpen={slashMenuOpen} 
            setIsOpen={setSlashMenuOpen} 
          />
        )}
      </div>
      {/* Add custom styles for Notion-like editor */}
      <style dangerouslySetInnerHTML={{ __html: `
        .ProseMirror {
          min-height: 300px;
          color: black;
          font-size: 16px;
          line-height: 1.6;
          padding: 0.5rem;
        }
        .ProseMirror p {
          margin: 0.5em 0;
        }
        .ProseMirror h1 {
          font-size: 1.875em;
          margin-top: 1em;
          margin-bottom: 0.5em;
          font-weight: 600;
        }
        .ProseMirror h2 {
          font-size: 1.5em;
          margin-top: 0.83em;
          margin-bottom: 0.5em;
          font-weight: 600;
        }
        .ProseMirror h3 {
          font-size: 1.25em;
          margin-top: 0.67em;
          margin-bottom: 0.5em;
          font-weight: 600;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .ProseMirror blockquote {
          border-left: 3px solid #ddd;
          margin-left: 0;
          margin-right: 0;
          padding-left: 1em;
          color: #666;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          color: #adb5bd;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .ProseMirror:focus {
          outline: none;
        }
        .ProseMirror code {
          background-color: rgba(97, 97, 97, 0.1);
          color: #616161;
          padding: 0.15em 0.25em;
          border-radius: 0.25em;
        }
        .ProseMirror pre {
          background: #0D0D0D;
          color: #FFF;
          padding: 0.75rem 1rem;
          border-radius: 0.5rem;
        }
        .ProseMirror pre code {
          color: inherit;
          padding: 0;
          background: none;
          font-size: 0.8rem;
        }
        .ProseMirror img {
          max-width: 100%;
          height: auto;
        }
        .ProseMirror hr {
          border: none;
          border-top: 2px solid rgba(13, 13, 13, 0.1);
          margin: 2rem 0;
        }
        .ProseMirror ul[data-type="taskList"] {
          list-style: none;
          padding: 0;
        }
        .ProseMirror ul[data-type="taskList"] li {
          display: flex;
          align-items: flex-start;
        }
        .ProseMirror ul[data-type="taskList"] li > label {
          margin-right: 0.5rem;
          user-select: none;
        }
        .ProseMirror ul[data-type="taskList"] li > div {
          flex: 1;
        }
      `}} />
    </div>
  );
};

export default TipTapEditor;
